import {
    Component,
    Input,
    InputSignal,
    OnInit,
    Signal,
    WritableSignal,
    computed,
    effect,
    input,
    signal,
    inject,
    DestroyRef,
    untracked,
} from '@angular/core';
import { CommonModule, formatCurrency } from '@angular/common';
import { IqRateNtlStateService } from '../state';
import {
    EquipmentTypes,
    RateResponse,
} from '../models';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import { DatiqLoaderModule } from '@dat/datiq-components-pkg/loader';
import { DatiqScreenModule } from '@dat/datiq-components-pkg/screen';
import { isNil, isEmpty } from 'ramda';

import { IqEventBusBrokerService } from '@dat/iq-event-bus';

@Component({
    selector: 'iq-rate-ntl-card',
    standalone: true,
    imports: [CommonModule, DatiqTemplateModule, DatiqLoaderModule, DatiqScreenModule],
    templateUrl: './iq-rate-ntl-card.component.html',
    styleUrl: './iq-rate-ntl-card.component.scss',
})
export class IqRateNtlCardComponent {
    id: InputSignal<string> = input.required();
    rateType: InputSignal<string> = input.required();
    equipmentType: WritableSignal<EquipmentTypes> = signal(EquipmentTypes.Van);

    nationalData: Signal<RateResponse> = computed(() => {
        if (!this.equipmentType() || !this.rateType()) { return };

        return this._state.nationalRates()[this.equipmentType()][this.rateType()];
    });

    hasNationalData: Signal<boolean> = computed(() => {
        return !isNil(this.equipmentType()) && 
        !isNil(this.rateType()) && 
        !isEmpty(this.nationalData());
    });

    errored: Signal<boolean> = computed(() => {        
        return this._state.nationalRates()[this.equipmentType()].errored;
    });

    loading: Signal<boolean> = computed(() => {
        return !this.nationalData() && !this.errored();
    });

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _destroyRef: DestroyRef = inject(DestroyRef);
    private _state: IqRateNtlStateService = inject(IqRateNtlStateService);

    constructor() {
        effect(() => {
            const id = this.id();

            untracked(() => {
                const handlers = this._bus.registerMap({
                    setLandingEquipmentType: {
                        callback: (payload: any) => {
                            this.equipmentType.set(payload.equipmentType);
                        }
                    }
                });

                this._destroyRef.onDestroy(() => {
                    this._bus.unregisterMap(handlers);
                });
            });
        });

        effect(() => {
            const currentData = this.nationalData();

            untracked(() => {
                if (!currentData) { return null };

                const updatedDate = this.getLastUpdatedDate(currentData);
 
                this._bus.dispatch({
                    name: `setNationalRateUpdatedDate`,
                    payload: {
                        label: updatedDate,
                    },
                });
            });
        });
    }


    rate: Signal<string> = computed(() => {
        const milesArray = this.nationalData()?.national?.rates?.mile;

        const rateLabel: string = this.getCurrencyString(
            milesArray[milesArray.length - 1]?.average,
        );
        const rateChangeLabel: string = this.getCurrencyString(
            this.nationalData()?.national?.changeRate,
        );

        //Change Return To Just Be Raw Response NOT Html?
        return `**<span class="headline-large-strong">${rateLabel}</span>** <span class="font-body-2 text-dat-grey-500 ml-2">(${rateChangeLabel})</span>`;
    });

    trend: Signal<string> = computed(() => {
        const icon: string = this.trendIcon(
            this.nationalData()?.national?.isChangePositive,
            this.nationalData()?.national?.isChangeNegative,
            this.nationalData()?.national?.isChangeNeutral,
        );
        const message: string = this.trendMessage(
            this.nationalData()?.national?.isChangePositive,
            this.nationalData()?.national?.isChangeNegative,
            this.nationalData()?.national?.isChangeNeutral,
        );

        return `<span class="text-dat-grey-500 material-symbols-outlined text-[14px] leading-[20px]">${icon}</span> <span class="font-body-2">${message}</span>`;
    });

    getCurrencyString: (num: number) => string = (num: number): string =>
        formatCurrency(num, 'en-US', '$', 'USD', '1.2-2');

    trendIcon(
        isChangePositive: boolean,
        isChangeNegative: boolean,
        isChangeNeutral: boolean,
    ): string {
        return isChangePositive
            ? 'trending_up'
            : isChangeNegative
            ? 'trending_down'
            : isChangeNeutral
            ? 'trending_flat'
            : '';
    }

    trendMessage(
        isChangePositive: boolean,
        isChangeNegative: boolean,
        isChangeNeutral: boolean,
    ): string {
        return isChangePositive
            ? 'Rate is increasing'
            : isChangeNegative
            ? 'Rate is decreasing'
            : isChangeNeutral
            ? 'Rate is neutral'
            : '';
    }

    getLastUpdatedDate(currentRate): string {
        if (!currentRate) { return null };

        const lastUpdatedDate = new Date(currentRate.national.rates.mile.slice(-1).pop().date).toLocaleDateString('en-US');
        return lastUpdatedDate;
    }
}
